import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import { concatMap } from 'lib/arrayUtils';
import pipe from 'lib/pipe';

import getPackagesForZone from '../getPackagesForZone';

const addPackageIndex = (pkgs) => pkgs.map((pkg, i) => ({ ...pkg, packageIndex: i }));

const fullBleedPackageTypes = [
  'leadRectangle',
  'leadRectanglePlusMosaic',
  'magazineLead',
  'collectionsLead',
  'coverSpread',
  'leadSectionFront',
  'showLead',
  'slimLead',
  'threeFeatured',
  'threeFeaturedPlusList',
  'waffle',
  'bigHeadline',
  'bigStoryTakeover',
  'bigStoryTakeoverPlus2',
  'bigStoryTakeoverPlus3',
  'bigStoryTakeoverPlus4',
  'entityHub',
];

const isFullBleed = (pkg) => {
  const firstItemUrl = get(pkg, 'items[0].computedValues.url', '');
  if (firstItemUrl && pkg.type === 'embed' && firstItemUrl.includes('/ramen-layout_full-bleed')) {
    return true;
  }
  if (fullBleedPackageTypes.includes(pkg.type)) {
    return true;
  }
  return false;
};

const sliceFullBleedLayouts = (allPkgs) => allPkgs.reduce((layouts, pkg, i) => {
  const fullBleed = isFullBleed(pkg);
  if (fullBleed) {
    layouts.push({ type: 'fullBleed', packages: [pkg], i });
  } else {
    if (!layouts.length || layouts[layouts.length - 1].type !== 'fullWidth') {
      layouts.push({ type: 'fullWidth', packages: [], i });
    }
    layouts[layouts.length - 1].packages.push(pkg);
  }
  return layouts;
}, []);

const spliceMidResponsiveAd = (vertical) => (layouts) => {
  if (['news', 'today', 'msnbc'].includes(vertical)) return layouts;
  let hasInsertedAd = false;
  return layouts.map((layout) => ({
    ...layout,
    packages: hasInsertedAd ? layout.packages : concatMap(layout.packages, (pkg) => {
      // Splice midresponsive ad after first 7up on page
      if (!hasInsertedAd && pkg.type === 'sevenUp') {
        hasInsertedAd = true;
        return [pkg, { type: 'midresponsiveAd', zone: 1 }];
      }
      return pkg;
    }),
  }));
};

const getPackageComponents = (context) => (layouts) => layouts.map((layout) => ({
  ...layout,
  packages: getPackagesForZone(1, layout.packages, context),
}));

const FullWidth = ({ packages }, context) => {
  // Package types used for PackageContext before splicing into separate layouts
  const zonePackageTypes = packages.map((pkg) => pkg.type);

  const fullWidthPipeline = pipe(
    addPackageIndex,
    sliceFullBleedLayouts,
    spliceMidResponsiveAd(context.vertical),
    getPackageComponents({ ...context, zonePackageTypes }),
  );

  const { layoutIndex } = context;

  const layouts = fullWidthPipeline(packages);

  return layouts.map((layout) => {
    if (layout.type === 'fullWidth') {
      return (
        <div
          key={layout.i}
          className={classNames(
            'fullWidth layout-grid-container',
            `layout-index-${layoutIndex + 1}`,
          )}
        >
          <div className="layout-grid-item">
            {layout.packages}
          </div>
        </div>
      );
    } if (layout.type === 'fullBleed') {
      return (
        <div
          key={layout.i}
          className={classNames(
            'fullBleed',
            `layout-index-${layoutIndex + 1}`,
          )}
        >
          {layout.packages}
        </div>
      );
    }
    return null;
  });
};

FullWidth.propTypes = {
  packages: PropTypes.arrayOf(CustomPropTypes.package).isRequired,
};

FullWidth.contextTypes = {
  ...CustomPropTypes.layoutContext,
};

export default FullWidth;
